import type {
    CostPricePayload,
    CostPriceType,
    MerchantProductDetailType,
    MerchantProductsActivityResponse,
    MerchantProductsImportResponse,
    MerchantProductsQueryParams,
    MerchantProductsResponse,
} from "@src/types";
import { privateApi } from "./client";

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

function buildMerchantProductsQuery(params: MerchantProductsQueryParams = {}) {
    const search = new URLSearchParams();
    const page = params.page ?? DEFAULT_PAGE;
    const size = params.size ?? DEFAULT_SIZE;

    search.set("page", page.toString());
    search.set("size", size.toString());

    if (params.search) {
        search.set("search", params.search);
    }

    if (params.available !== undefined && params.available !== null) {
        search.set("available", String(params.available));
    }

    if (typeof params.brand === "string" && params.brand.trim()) {
        search.set("brand", params.brand.trim());
    } else if (params.brandFlag !== undefined && params.brandFlag !== null) {
        search.set("brand", String(params.brandFlag));
    }

    if (params.is_brand !== undefined && params.is_brand !== null) {
        search.set("is_brand", String(params.is_brand));
    }

    return search.toString();
}

export interface MerchantProductsExportResult {
    blob: Blob;
    filename: string;
    contentType: string;
}

export async function getMerchantProductsByMerchantApi(
    merchantId: string,
    params: MerchantProductsQueryParams = {},
    signal?: AbortSignal,
): Promise<MerchantProductsResponse> {
    const query = buildMerchantProductsQuery(params);
    const endpoint = `/api/merchant-products/list/by-merchant/${merchantId}?${query}`;
    const { data } = await privateApi.get<MerchantProductsResponse>(endpoint, { signal });
    return data;
}

export async function getMerchantProductsActivityApi(
    merchantId: string,
    params: MerchantProductsQueryParams = {},
    signal?: AbortSignal,
): Promise<MerchantProductsActivityResponse> {
    const query = buildMerchantProductsQuery(params);
    const endpoint = `/api/merchant-products/list/by-merchant/${merchantId}/activity?${query}`;
    const { data } = await privateApi.get<MerchantProductsActivityResponse>(endpoint, { signal });
    return data;
}

export async function exportMerchantProductsApi(
    merchantId: string,
    params: MerchantProductsQueryParams = {},
    signal?: AbortSignal,
): Promise<MerchantProductsExportResult> {
    const query = buildMerchantProductsQuery(params);
    const endpoint = `/api/merchant-products/list/by-merchant/${merchantId}/export?${query}`;
    const response = await privateApi.get<Blob>(endpoint, {
        responseType: "blob",
        signal,
    });
    const filename = "товары.xlsx";
    const contentType =
        response.headers["content-type"] ??
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    return {
        blob: response.data,
        filename,
        contentType,
    };
}

export async function getMerchantProductDetailApi(
    merchantProductId: string,
    signal?: AbortSignal,
): Promise<MerchantProductDetailType> {
    const endpoint = `/api/merchant-products/${merchantProductId}`;
    const { data } = await privateApi.get<MerchantProductDetailType>(endpoint, { signal });
    return data;
}

export async function listCostPricesApi(
    merchantProductId: string,
    signal?: AbortSignal,
): Promise<CostPriceType[]> {
    const endpoint = `/api/merchant-products/${merchantProductId}/cost-prices`;
    const { data } = await privateApi.get<CostPriceType[]>(endpoint, { signal });
    return data;
}

export async function createCostPriceApi(
    merchantProductId: string,
    payload: CostPricePayload,
): Promise<CostPriceType> {
    const endpoint = `/api/merchant-products/${merchantProductId}/cost-prices`;
    const { data } = await privateApi.post<CostPriceType>(endpoint, payload);
    return data;
}

export async function updateCostPriceApi(
    costPriceId: string,
    payload: CostPricePayload,
): Promise<CostPriceType> {
    const endpoint = `/api/merchant-products/cost-prices/${costPriceId}`;
    const { data } = await privateApi.put<CostPriceType>(endpoint, payload);
    return data;
}

export async function deleteCostPriceApi(costPriceId: string): Promise<void> {
    const endpoint = `/api/merchant-products/cost-prices/${costPriceId}`;
    await privateApi.delete(endpoint);
}

export async function importMerchantProductsCostPricesApi(
    merchantId: string,
    file: File,
    signal?: AbortSignal,
): Promise<MerchantProductsImportResponse> {
    const endpoint = `/api/merchant-products/merchants/${merchantId}/cost-prices/import`;
    const formData = new FormData();
    formData.append("file", file, file.name);

    const { data } = await privateApi.post<MerchantProductsImportResponse>(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal,
    });

    return data;
}
